// Standalone and Wizard conversations have no pre-existing project id — Standalone's project is
// created conversationally with no REST API, and Wizard isn't project-scoped until build-out
// finishes. Chat sessions still require a projectId (POST /api/chat/sessions), so both are
// bucketed under a fixed sentinel id rather than a real project.
export const STANDALONE_PROJECT_ID = 'standalone';
export const WIZARD_PROJECT_ID = 'wizard';
// General, unscoped chat — same 'studio' sentinel the dashboard's "Ask AI" already uses
// (chat-session.controller.ts). No connectedAgent: the whole point is the coordinator picks
// freely among every agent the user has connected, exactly like it already does for
// analyticsContext-only dashboard questions. A real project can be attached to the conversation
// later (see SessionChatView's AttachProjectControl) — purely a per-message context.boardId from
// then on, no session-level field, no BE change.
export const STUDIO_PROJECT_ID = 'studio';

interface VirtualProject {
  id: string;
  label: string;
  connectedAgent?: string;
}

export const VIRTUAL_PROJECTS: VirtualProject[] = [
  { id: STUDIO_PROJECT_ID, label: 'General chat' },
  { id: STANDALONE_PROJECT_ID, label: 'Standalone (new project)', connectedAgent: 'StandaloneAgent' },
  { id: WIZARD_PROJECT_ID, label: 'Start from a spec (wizard)', connectedAgent: 'WizardAgent' },
];

export const isStandaloneProjectId = (id?: string | null): boolean => id === STANDALONE_PROJECT_ID;
export const isWizardProjectId = (id?: string | null): boolean => id === WIZARD_PROJECT_ID;
export const isStudioProjectId = (id?: string | null): boolean => id === STUDIO_PROJECT_ID;
export const isVirtualProjectId = (id?: string | null): boolean => VIRTUAL_PROJECTS.some((v) => v.id === id);

export const getVirtualProjectAgent = (id?: string | null): string | undefined =>
  VIRTUAL_PROJECTS.find((v) => v.id === id)?.connectedAgent;

export const getVirtualProjectLabel = (id?: string | null): string | undefined =>
  VIRTUAL_PROJECTS.find((v) => v.id === id)?.label;

// The routing ids above ('standalone', 'wizard') are exactly the `type` values
// POST /api/chat/sessions accepts — the server computes the same `${type}-${userId}` sentinel
// when `type` is sent without a `projectId`. Resolving it client-side here (instead of relying on
// the server default) lets FE use the same id consistently for both creating AND listing sessions
// (GET only accepts `projectId`, not `type`).
export const resolveApiProjectId = (routingId: string, userId: string): string =>
  isVirtualProjectId(routingId) ? `${routingId}-${userId}` : routingId;
